import React, { useContext, useEffect, useRef, useState } from 'react';
import style from './UserList.style';
import { faKey, faPortrait, faSpinner, faSquare, faSquareCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAlert } from 'react-alert';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledTooltip } from 'reactstrap';
import Swal from 'sweetalert2';
import { DepartmentContext } from 'DepartmentWrapper';
import moment from 'moment';

const SingleUser = ({
  user,
  selectedUser,
  index,
  setSelectedUser,
  filterBy,
  postUserInAuth,
  getUsers,
  hasUserDetailChanges,
  discardChanges,
}) => {
  const {
    UserPhotoUrl,
    UserFirst,
    UserLast,
    LastLoginDate,
    EnableAdmin,
    EnableAssistant,
    EnableEvaluator,
    EnablePointOfContact,
    LastLoggedIn,
    Auth0Id,
    LastLoggedInDepartment,
    Deleted,
    pk_User,
    PendingUserMigration,
    SingleSignOn,
  } = user;
  const [userStatusToolTipOpen, setUserStatusToolTipOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [lastPasswordResetPast, setLastPasswordResetPast] = useState(0);
  const [dontShowConfirmModal, setDontShowConfirmModal] = useState(false);
  const [showConfirmResendModal, setShowConfirmResendModal] = useState(false);
  const [showConfirmResetPasswordModal, setShowConfirmResetPasswordModal] = useState(false);

  const lastPasswordReset = useRef(null);
  const passwordResetChecker = useRef(null);
  const dContext = useContext(DepartmentContext);
  const alert = useAlert();
  const passwordCheckerExpiry = 30;

  useEffect(() => {
    getLastPasswordReset();
    initializePasswordChecker();
  }, []);

  useEffect(() => {
    if (lastPasswordResetPast > passwordCheckerExpiry) {
      clearInterval(passwordResetChecker.current);
      setLastPasswordResetPast(0);
    }
  }, [lastPasswordResetPast]);
  const initializePasswordChecker = () => {
    if (passwordResetChecker.current) {
      clearInterval(passwordResetChecker.current);
      setLastPasswordResetPast(0);
    }
    passwordResetChecker.current = setInterval(() => {
      if (lastPasswordReset.current) {
        const difference = moment().diff(lastPasswordReset.current, 'seconds');
        setLastPasswordResetPast(difference);
      }
    });
  };

  const getLastPasswordReset = () => {
    let passWordObject = localStorage.getItem('lastPasswordReset');

    if (passWordObject) {
      passWordObject = JSON.parse(passWordObject);
    } else {
      passWordObject = {};
    }

    const newLastPasswordReset = passWordObject
      ? passWordObject[`${pk_User}_${dContext.department.pk_Department}`]
      : null;

    lastPasswordReset.current = newLastPasswordReset ? moment(newLastPasswordReset) : null;
  };

  const handlePost = async (type) => {
    setDisableButton(true);
    const result = await postUserInAuth(pk_User, type);
    if (result.error === true) {
      alert.error(`Something went wrong saving ${UserLast}, ${UserFirst}. Please contact support`);
    } else {
      let passWordObject = localStorage.getItem('lastPasswordReset');
      if (passWordObject) {
        passWordObject = JSON.parse(passWordObject);
      } else {
        passWordObject = {};
      }
      alert.success(`${type === 'reset' ? 'Password reset' : 'Welcome email'} sent to ${UserFirst} ${UserLast}`);
      const newLastPasswordReset = moment();
      passWordObject[`${pk_User}_${dContext.department.pk_Department}`] = newLastPasswordReset;
      lastPasswordReset.current = newLastPasswordReset;
      localStorage.setItem('lastPasswordReset', JSON.stringify(passWordObject));
      initializePasswordChecker();
    }

    setDisableButton(false);
  };

  const renderConfirmResendModal = () => {
    const isSend = SingleSignOn !== true && LastLoggedIn === null && Auth0Id === null;

    return (
      <Modal isOpen={showConfirmResendModal} centered>
        <ModalHeader
          toggle={() => {
            setShowConfirmResendModal(false);
          }}
        >
          {isSend ? 'Send' : 'Resend'} Welcome Email
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              fontSize: 15,
              fontWeight: 600,
              textAlign: 'center',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {isSend ? 'Send' : 'Resend'} the welcome email to {UserFirst || ''} {UserLast || ''}?
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: 30, justifyContent: 'center' }}>
              <FontAwesomeIcon
                icon={dontShowConfirmModal ? faSquareCheck : faSquare}
                style={{ fontSize: 15, cursor: 'pointer' }}
                onClick={() => {
                  setDontShowConfirmModal(!dontShowConfirmModal);
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: 'auto',
                fontSize: 13,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              Don't ask me again for 5 minutes...
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', width: '100%' }}>
            {' '}
            <Button
              style={{ width: 120 }}
              onClick={() => {
                setDontShowConfirmModal(false);
                setShowConfirmResendModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color={'primary'}
              style={{ width: 120 }}
              onClick={() => {
                if (dontShowConfirmModal) {
                  localStorage.setItem('lastResendResetDontShowConfirmModal', Date.now());
                }
                setDontShowConfirmModal(false);
                setShowConfirmResendModal(false);
                handlePost('welcome');
              }}
            >
              {isSend ? 'Send' : 'Resend'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  const renderConfirmResetPasswordModal = () => {
    return (
      <Modal isOpen={showConfirmResetPasswordModal} centered>
        <ModalHeader
          toggle={() => {
            setShowConfirmResetPasswordModal(false);
          }}
        >
          Reset Password
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              fontSize: 15,
              fontWeight: 600,
              textAlign: 'center',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            Reset the password for {UserFirst || ''} {UserLast || ''}?
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: 30, justifyContent: 'center' }}>
              <FontAwesomeIcon
                icon={dontShowConfirmModal ? faSquareCheck : faSquare}
                style={{ fontSize: 15, cursor: 'pointer' }}
                onClick={() => {
                  setDontShowConfirmModal(!dontShowConfirmModal);
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: 'auto',
                fontSize: 13,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              Don't ask me again for 5 minutes...
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', width: '100%' }}>
            {' '}
            <Button
              style={{ width: 120 }}
              onClick={() => {
                setDontShowConfirmModal(false);
                setShowConfirmResetPasswordModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color={'primary'}
              style={{ width: 120 }}
              onClick={() => {
                if (dontShowConfirmModal) {
                  localStorage.setItem('lastResendResetDontShowConfirmModal', Date.now());
                }
                handlePost('reset');
                setShowConfirmResetPasswordModal(false);
                setDontShowConfirmModal(false);
              }}
            >
              Reset
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  const renderUserStatusButton = () => {
    const renderFontAwesomeIcon = () => {
      return <FontAwesomeIcon icon={disableButton ? faSpinner : faKey} spin={disableButton} />;
    };
    const lastPasswordResetDifference = lastPasswordReset.current
      ? moment().diff(lastPasswordReset.current, 'seconds')
      : 0;

    if (SingleSignOn === true) {
      return (
        <>
          <Button
            color="primary"
            disabled={true}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const previousTime = localStorage.getItem('lastResendResetDontShowConfirmModal');

              if (!previousTime || (previousTime && Date.now() - previousTime > 3 * 60 * 1000)) {
                setShowConfirmResendModal(true);
              } else {
                handlePost('welcome');
              }

              // Swal.fire({
              //   icon: 'warning',
              //   text: `Send welcome to ${UserFirst || ''} ${UserLast}?`,
              //   showCancelButton: true,
              //   confirmButtonText: `Resend`,
              // }).then((result) => {
              //   /* Read more about isConfirmed, isDenied below */
              //   if (result.value) {
              //     handlePost('welcome');
              //   }
              // });
            }}
            id={`tool_tip_resend_welcome${pk_User}`}
          >
            {renderFontAwesomeIcon()}
          </Button>
          <UncontrolledTooltip
            flip={false}
            placement="right"
            modifiers={{ flip: { behavior: ['right'] }, preventOverflow: { boundariesElement: 'viewport' } }}
            target={`tool_tip_resend_welcome${pk_User}`}
          >
            This user logs in with Single Sign On
          </UncontrolledTooltip>
        </>
      );
    }

    // if user has logged in then we can assume they have an auth0 id
    if (LastLoggedIn !== null) {
      return (
        <>
          <Button
            color="primary"
            disabled={disableButton || (lastPasswordReset.current && lastPasswordResetDifference < 10)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const previousTime = localStorage.getItem('lastResendResetDontShowConfirmModal');

              if (!previousTime || (previousTime && Date.now() - previousTime > 3 * 60 * 1000)) {
                setShowConfirmResetPasswordModal(true);
              } else {
                handlePost('reset');
              }

              // Swal.fire({
              //   icon: 'warning',
              //   text: `Resend password reset to ${UserFirst} ${UserLast}?`,
              //   showCancelButton: true,
              //   confirmButtonText: `Reset`,
              // }).then((result) => {
              //   /* Read more about isConfirmed, isDenied below */
              //   if (result.value) {
              //     handlePost('reset');
              //   }
              // });
            }}
            id={`tool_tip_reset${pk_User}`}
          >
            {renderFontAwesomeIcon()}
          </Button>
          <UncontrolledTooltip
            flip={false}
            placement="right"
            modifiers={{ flip: { behavior: ['right'] }, preventOverflow: { boundariesElement: 'viewport' } }}
            target={`tool_tip_reset${pk_User}`}
          >
            Send password reset
          </UncontrolledTooltip>
        </>
      );
    }

    // if auth0 id is not null but they have no previously logged in date they'res setup in auth0 but have
    // never accessed their account
    if (Auth0Id !== null) {
      return (
        <>
          <Button
            color="warning"
            disabled={disableButton || (lastPasswordReset.current && lastPasswordResetDifference < 10)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const previousTime = localStorage.getItem('lastResendResetDontShowConfirmModal');

              if (!previousTime || (previousTime && Date.now() - previousTime > 3 * 60 * 1000)) {
                setShowConfirmResendModal(true);
              } else {
                handlePost('welcome');
              }
              // Swal.fire({
              //   icon: 'warning',
              //   text: `Send welcome to ${UserFirst || ''} ${UserLast}?`,
              //   showCancelButton: true,
              //   confirmButtonText: `Resend`,
              // }).then((result) => {
              //   /* Read more about isConfirmed, isDenied below */
              //   if (result.value) {
              //     handlePost('welcome');
              //   }
              // });
            }}
            id={`tool_tip_resend_welcome${pk_User}`}
          >
            {renderFontAwesomeIcon()}
          </Button>
          <UncontrolledTooltip
            flip={false}
            placement="right"
            modifiers={{ flip: { behavior: ['right'] }, preventOverflow: { boundariesElement: 'viewport' } }}
            target={`tool_tip_resend_welcome${pk_User}`}
          >
            User has never logged in (resend welcome)
          </UncontrolledTooltip>
        </>
      );
    }

    // if neither condition above is met, they are not registered in auth0

    return (
      <>
        <Button
          color="danger"
          disabled={disableButton || (lastPasswordReset.current && lastPasswordResetDifference < 10)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const previousTime = localStorage.getItem('lastResendResetDontShowConfirmModal');

            if (!previousTime || (previousTime && Date.now() - previousTime > 3 * 60 * 1000)) {
              setShowConfirmResendModal(true);
            } else {
              handlePost('welcome');
            }
          }}
          id={`tool_tip_welcome${pk_User}`}
        >
          {renderFontAwesomeIcon()}
        </Button>{' '}
        <UncontrolledTooltip
          flip={false}
          placement="right"
          modifiers={{ flip: { behavior: ['right'] }, preventOverflow: { boundariesElement: 'viewport' } }}
          target={`tool_tip_welcome${pk_User}`}
        >
          Send welcome email
        </UncontrolledTooltip>
      </>
    );
  };

  if (Deleted && filterBy !== 'Deactivated') {
    return;
  }
  let Role = '';

  const roleLabels = {
    EnableAssistant: 'Assistant',
    EnableAdmin: 'Administrator',
    EnableEvaluator: 'Evaluator',
    EnablePointOfContact: 'Point of Contact',
  };

  const affixRole = (role, name) => {
    if (role) {
      if (Role === '') {
        Role = roleLabels[name];
      } else {
        Role += `, ${roleLabels[name]}`;
      }
    }
  };

  affixRole(EnableAdmin, 'EnableAdmin');
  affixRole(EnableAssistant, 'EnableAssistant');
  affixRole(EnableEvaluator, 'EnableEvaluator');
  affixRole(EnablePointOfContact, 'EnablePointOfContact');

  let backgroundColor = null;
  if (selectedUser && selectedUser.pk_User == user.pk_User) {
    backgroundColor = '#d9f5ff';
  } else if (index % 2 == 0) {
    backgroundColor = '#c7cbd1';
  }

  return (
    <div
      key={pk_User}
      id={`user_${pk_User}`}
      style={{ ...style.itemRow, backgroundColor }}
      onClick={() => {
        if (hasUserDetailChanges) {
          Swal.fire({
            title: 'Warning!',
            text: 'There are unsaved changes. Are you sure want to load a different user?',
            // showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: `Discard`,
            // denyButtonText: `Cancel`,
          }).then((result) => {
            /* Read more about isConfirmed, isDenied below */
            if (result.value) {
              if (discardChanges) {
                discardChanges();
                setSelectedUser(user);
              }
            }
          });
          return;
        } else {
          setSelectedUser(user);
        }
      }}
    >
      <div>
        {UserPhotoUrl ? (
          <img src={UserPhotoUrl} height={50} style={style.imgPlaceHolder} />
        ) : (
          <FontAwesomeIcon style={style.imgPlaceHolder} icon={faPortrait} />
        )}
      </div>
      <div style={style.candidateInfo}>
        <div>{`${UserLast ? `${UserLast}, ` : ''} ${UserFirst ? UserFirst : ''}`}</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            fontSize: 11,
            fontWeight: 'bold',
            height: !Role ? 20 : null,
            color: !Role ? '#b10542' : 'black',
          }}
        >
          {Role || 'No Role'}
        </div>
      </div>

      <div>{renderUserStatusButton()}</div>
      {renderConfirmResendModal()}
      {renderConfirmResetPasswordModal()}
    </div>
  );
};

export default SingleUser;
